import React, { useState } from "react";
import { Link } from "react-router-dom";
import {Badge} from "@mui/material";
import {PermIdentity} from "@mui/icons-material";

// ─────────────────────────────────────────────
//  Conditional Rendering
//  Chapter 3 · State & Events
//  Author: Ajao Hazzan · Full-Stack Software Engineer
// ─────────────────────────────────────────────

const PRIMARY = "#61DAFB";
const BG = "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1600&q=80";

/* ── Code snippets stored as template literals ── */
const CODE_0 = `function Dashboard({ user, isLoading, error, notifications }) {
  // ── 1. if/else (outside JSX — for complex conditions)
  if (isLoading) return <Spinner />;
  if (error)     return <ErrorBanner message={error} />;
  if (!user)     return <Navigate to="/login" />;

  return (
    <div>
      {/* ── 2. Ternary — for if/else inside JSX */}
      <h1>{user.isPremium ? '⭐ Premium Dashboard' : 'Dashboard'}</h1>

      {/* ── 3. Logical && — when you only need the 'true' branch */}
      {notifications.length > 0 && (
        <NotificationBell count={notifications.length} />
      )}

      {/* ── 4. Nullish coalescing ?? — default value */}
      <p>{user.bio ?? 'No bio provided'}</p>

      {/* ── 5. switch via IIFE — for many conditions */}
      {(() => {
        switch (user.role) {
          case 'admin':  return <AdminPanel />;
          case 'manager': return <ManagerPanel />;
          default:       return <UserPanel />;
        }
      })()}
    </div>
  );
}`;

const CODE_1 = `function UserList() {
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    fetch('/api/users')
      .then(r => r.json())
      .then(data => { setUsers(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  // Guard clauses — handle each state separately
  if (loading) return <div className="loading"><Spinner /> Loading users...</div>;
  if (error)   return <div className="error">Error: {error} <button onClick={retry}>Retry</button></div>;
  if (users.length === 0) return <div className="empty">No users found.</div>;

  // Happy path — data is ready
  return (
    <ul>
      {users.map(user => <UserCard key={user.id} user={user} />)}
    </ul>
  );
}`;


/* ── Styles ── */
const styles = {
  page:    { minHeight: "100vh", backgroundImage: `linear-gradient(rgba(10,12,30,0.94),rgba(10,12,30,0.97)),url(${BG})`, backgroundSize: "cover", backgroundAttachment: "fixed", color: "#fff", fontFamily: "'Segoe UI',sans-serif", paddingBottom: "60px" },
  header:  { background: "rgba(10,12,30,0.95)", borderBottom: "1px solid rgba(97,218,251,0.15)", padding: "18px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", position: "sticky", top: 0, zIndex: 100 },
  badge:   { background: "rgba(97,218,251,0.12)", border: "1px solid rgba(97,218,251,0.3)", color: PRIMARY, padding: "4px 14px", borderRadius: "999px", fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.06em" },
  backBtn: { background: "rgba(97,218,251,0.1)", border: "1px solid rgba(97,218,251,0.25)", color: PRIMARY, padding: "8px 18px", borderRadius: "999px", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "6px" },
  hero:    { padding: "48px 32px 24px", maxWidth: "900px", margin: "0 auto" },
  title:   { fontSize: "clamp(1.6rem,4vw,2.4rem)", fontWeight: 800, color: PRIMARY, marginBottom: "10px", lineHeight: 1.2 },
  desc:    { color: "rgba(255,255,255,0.55)", fontSize: "1rem", lineHeight: 1.7, maxWidth: "680px", marginBottom: "32px" },
  content: { maxWidth: "900px", margin: "0 auto", padding: "0 32px 40px" },
  section: { marginBottom: "36px" },
  sh:      { fontSize: "1.1rem", fontWeight: 700, color: "#fff", marginBottom: "10px", borderLeft: "3px solid " + PRIMARY, paddingLeft: "12px" },
  body:    { color: "rgba(255,255,255,0.72)", lineHeight: 1.85, fontSize: "0.95rem", marginBottom: "6px" },
  pre:     { background: "#0D0F1A", border: "1px solid rgba(97,218,251,0.15)", borderLeft: "3px solid " + PRIMARY, borderRadius: "10px", padding: "20px 22px", overflowX: "auto", margin: "14px 0", fontSize: "0.83rem", lineHeight: 1.7, color: "#CDD6F4", fontFamily: "Courier New,monospace", whiteSpace: "pre" },
  qBox:    { background: "rgba(97,218,251,0.04)", border: "1px solid rgba(97,218,251,0.12)", borderRadius: "10px", padding: "18px 22px", marginTop: "8px" },
  qTitle:  { color: PRIMARY, fontWeight: 700, fontSize: "0.82rem", letterSpacing: "0.08em", marginBottom: "10px" },
  qItem:   { color: "rgba(255,255,255,0.65)", lineHeight: 1.8, fontSize: "0.92rem", marginBottom: "4px" },
  footer:  { maxWidth: "900px", margin: "0 auto", padding: "24px 32px 0", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" },
  credit:  { color: "rgba(255,255,255,0.25)", fontSize: "0.75rem" },
};

/* ── Component ── */
export default function ConditionalRendering() {
  const [copied, setCopied] = useState(null);

  function copyCode(codeStr, idx) {
    navigator.clipboard?.writeText(codeStr).then(() => {
      setCopied(idx);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <div style={styles.page}>

      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Dashboard</Link>
          <span style={styles.badge}>Chapter 3 · State & Events</span>
        </div>
        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>
          By <strong style={{ color: PRIMARY }}>Ajao Hazzan</strong> · Full-Stack Software Engineer
        </span>
      </header>

      {/* Hero */}
      <div style={styles.hero}>
        <h1 style={styles.title}>Conditional Rendering</h1>
        <p style={styles.desc}>Control what gets rendered based on state, props, or any JavaScript condition.</p>
      </div>

      {/* Content */}
      <div style={styles.content}>

      /* ── All Conditional Rendering Techniques ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>All Conditional Rendering Techniques</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "React gives you several ways to conditionally render UI. Choose based on readability and complexity." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_0, 0)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 0 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_0)}</pre>
        </div>
      </div>

      /* ── Loading States Pattern ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Loading States Pattern</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "A clean, reusable pattern for showing loading, error, empty, and content states." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_1, 1)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 1 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_1)}</pre>
        </div>
      </div>

      /* ── Practice Questions ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Practice Questions</h2>
        <div style={styles.qBox}>
          <div style={styles.qTitle}>📝 PRACTICE QUESTIONS</div>
          <div style={styles.qItem}>✦ 1. What is the risk of using && with a number (e.g. &#123;count && <Badge />&#125;)?</div>
          <div style={styles.qItem}>✦ 2. When should you use a ternary vs logical &&?</div>
          <div style={styles.qItem}>✦ 3. How do you conditionally apply a CSS class?</div>
          <div style={styles.qItem}>✦ 4. What does \'short-circuit evaluation\' mean in JSX?</div>
            <div style={styles.qItem}>✦ 5. Build a <PermIdentity> component that only renders children if the user has the required role.</PermIdentity></div>
        </div>
      </div>

        {/* Footer */}
        <div style={styles.footer}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Back to React Dashboard</Link>
          <span style={styles.credit}>Mastering React · © 2024 Ajao Hazzan</span>
        </div>
      </div>

    </div>
  );
}
